import React, { useState, useEffect } from "react";
import { NavLink, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, Globe, ChevronDown, Droplets, Flame, Hammer, Building2, LayoutGrid, ArrowRight, Mail } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { CONTACT_INFO, SERVICE_KEYS, SERVICE_META } from "../lib/data";
import { buildQuoteMailto } from "../lib/mailto";

const LANGS = ["de", "en", "fr"];
const SERVICE_ICONS = { Droplets, Flame, Hammer, Building2 };

export const Header = () => {
  const { lang, setLang, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navLinks = [
    { to: "/", label: t.nav.home, testid: "nav-link-home" },
    { to: "/dienstleistungen", label: t.nav.services, testid: "nav-link-services" },
    { to: "/referenzen", label: t.nav.references, testid: "nav-link-references" },
    { to: "/ueber-uns", label: t.nav.about, testid: "nav-link-about" },
    { to: "/kontakt", label: t.nav.contact, testid: "nav-link-contact" },
  ];

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-40 transition-all duration-300 ${
        scrolled
          ? "bg-white/80 backdrop-blur-xl border-b border-slate-200/70 shadow-sm"
          : "bg-white/60 backdrop-blur-md border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" data-testid="header-logo" className="flex items-center gap-3 group">
            <div className="relative h-12 w-12 rounded-full overflow-hidden ring-1 ring-slate-200 bg-white">
              <img
                src={CONTACT_INFO.logo}
                alt="SHK Haustechnik GmbH"
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
            </div>
            <div className="hidden sm:flex flex-col leading-tight">
              <span className="font-display font-black text-[#0022FF] text-base tracking-tight">
                SHK HAUSTECHNIK
              </span>
              <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">
                Sanitär · Heizung · Lüftung
              </span>
            </div>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => {
              // Special dropdown for Services
              if (link.to === "/dienstleistungen") {
                return (
                  <div
                    key={link.to}
                    className="relative"
                    onMouseEnter={() => setServicesOpen(true)}
                    onMouseLeave={() => setServicesOpen(false)}
                  >
                    <NavLink
                      to={link.to}
                      data-testid={link.testid}
                      className={({ isActive }) =>
                        `relative inline-flex items-center gap-1 px-4 py-2 text-sm font-semibold transition-colors ${
                          isActive ? "text-[#0022FF]" : "text-slate-700 hover:text-[#0022FF]"
                        }`
                      }
                    >
                      {({ isActive }) => (
                        <>
                          <span>{link.label}</span>
                          <ChevronDown
                            className={`h-3.5 w-3.5 transition-transform duration-300 ${
                              servicesOpen ? "rotate-180" : ""
                            }`}
                          />
                          {isActive && (
                            <motion.span
                              layoutId="active-nav"
                              className="absolute -bottom-1 left-4 right-7 h-[2px] bg-[#0022FF] rounded-full"
                            />
                          )}
                        </>
                      )}
                    </NavLink>

                    <AnimatePresence>
                      {servicesOpen && (
                        <motion.div
                          initial={{ opacity: 0, y: 6, scale: 0.98 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: 6, scale: 0.98 }}
                          transition={{ duration: 0.18 }}
                          className="absolute left-1/2 -translate-x-1/2 top-full pt-4 z-50"
                          data-testid="services-dropdown"
                        >
                          <div className="w-[640px] rounded-3xl border border-slate-200 bg-white shadow-2xl overflow-hidden">
                            <div className="grid grid-cols-2 gap-1 p-3">
                              {SERVICE_KEYS.map((k) => {
                                const meta = SERVICE_META[k];
                                const Icon = SERVICE_ICONS[meta.icon];
                                const svc = t.services[k];
                                return (
                                  <Link
                                    key={k}
                                    to={`/dienstleistungen/${k}`}
                                    data-testid={`dropdown-service-${k}`}
                                    onClick={() => setServicesOpen(false)}
                                    className="group flex items-start gap-3 rounded-2xl p-4 hover:bg-slate-50 transition-colors"
                                  >
                                    <div
                                      className="h-11 w-11 rounded-xl flex items-center justify-center shrink-0 transition-transform group-hover:scale-110"
                                      style={{ backgroundColor: `${meta.color}15`, color: meta.color }}
                                    >
                                      <Icon className="h-5 w-5" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="font-display font-bold text-slate-900 text-sm group-hover:text-[#0022FF] transition-colors">
                                        {svc.name}
                                      </div>
                                      <div className="text-xs text-slate-500 mt-0.5 line-clamp-1">
                                        {svc.short}
                                      </div>
                                    </div>
                                  </Link>
                                );
                              })}
                            </div>
                            <Link
                              to="/dienstleistungen"
                              data-testid="dropdown-service-all"
                              onClick={() => setServicesOpen(false)}
                              className="flex items-center justify-between gap-3 bg-slate-50 px-6 py-4 border-t border-slate-200 hover:bg-slate-100 transition-colors group"
                            >
                              <div className="flex items-center gap-3">
                                <div className="h-9 w-9 rounded-lg bg-[#0022FF] text-white flex items-center justify-center">
                                  <LayoutGrid className="h-4 w-4" />
                                </div>
                                <div>
                                  <div className="font-display font-bold text-slate-900 text-sm">
                                    {t.services.allServicesTitle}
                                  </div>
                                  <div className="text-xs text-slate-500">{t.services.subtitle.split(".")[0]}.</div>
                                </div>
                              </div>
                              <ArrowRight className="h-4 w-4 text-[#0022FF] group-hover:translate-x-1 transition-transform" />
                            </Link>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              }
              return (
                <NavLink
                  key={link.to}
                  to={link.to}
                  data-testid={link.testid}
                  end={link.to === "/"}
                  className={({ isActive }) =>
                    `relative px-4 py-2 text-sm font-semibold transition-colors ${
                      isActive ? "text-[#0022FF]" : "text-slate-700 hover:text-[#0022FF]"
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      <span>{link.label}</span>
                      {isActive && (
                        <motion.span
                          layoutId="active-nav"
                          className="absolute -bottom-1 left-4 right-4 h-[2px] bg-[#0022FF] rounded-full"
                        />
                      )}
                    </>
                  )}
                </NavLink>
              );
            })}
          </nav>

          {/* Right side */}
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Language switcher */}
            <div className="relative">
              <button
                data-testid="language-toggle"
                onClick={() => setLangOpen((v) => !v)}
                className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white/80 px-3 py-1.5 text-xs font-semibold uppercase text-slate-700 hover:border-[#0022FF] hover:text-[#0022FF] transition-colors"
              >
                <Globe className="h-3.5 w-3.5" />
                {lang}
              </button>
              <AnimatePresence>
                {langOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    className="absolute right-0 mt-2 w-28 rounded-xl border border-slate-200 bg-white shadow-xl overflow-hidden"
                  >
                    {LANGS.map((l) => (
                      <button
                        key={l}
                        data-testid={`language-option-${l}`}
                        onClick={() => {
                          setLang(l);
                          setLangOpen(false);
                        }}
                        className={`block w-full text-left px-4 py-2 text-xs font-semibold uppercase transition-colors ${
                          lang === l ? "bg-[#0022FF] text-white" : "text-slate-700 hover:bg-slate-100"
                        }`}
                      >
                        {l}
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <a
              href={buildQuoteMailto(lang)}
              data-testid="header-email-btn"
              title={t.nav.emailQuote}
              className="hidden sm:inline-flex items-center justify-center h-10 w-10 rounded-full border border-slate-300 bg-white text-slate-700 hover:border-[#0022FF] hover:text-[#0022FF] transition-colors"
            >
              <Mail className="h-4 w-4" />
            </a>

            <a
              href={`tel:${CONTACT_INFO.phoneClean}`}
              data-testid="header-call-btn"
              className="hidden sm:inline-flex items-center gap-2 rounded-full btn-shk-primary px-5 py-2.5 text-sm font-semibold"
            >
              <Phone className="h-4 w-4" />
              {t.nav.callNow}
            </a>

            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setMobileOpen((v) => !v)}
              className="lg:hidden inline-flex items-center justify-center h-10 w-10 rounded-full border border-slate-200 text-slate-700"
            >
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden overflow-hidden border-t border-slate-200 bg-white"
          >
            <nav className="px-4 sm:px-6 py-4 flex flex-col gap-1">
              {navLinks.map((link) => {
                if (link.to === "/dienstleistungen") {
                  return (
                    <div key={link.to} className="flex flex-col">
                      <div className="flex items-stretch gap-1">
                        <NavLink
                          to={link.to}
                          data-testid={`mobile-${link.testid}`}
                          onClick={() => setMobileOpen(false)}
                          className={({ isActive }) =>
                            `flex-1 px-4 py-3 rounded-xl text-base font-semibold transition-colors ${
                              isActive ? "bg-[#0022FF] text-white" : "text-slate-700 hover:bg-slate-100"
                            }`
                          }
                        >
                          {link.label}
                        </NavLink>
                        <button
                          data-testid="mobile-services-toggle"
                          onClick={() => setMobileServicesOpen((v) => !v)}
                          className="px-3 rounded-xl text-slate-700 hover:bg-slate-100"
                          aria-label="Toggle services"
                        >
                          <ChevronDown
                            className={`h-5 w-5 transition-transform ${mobileServicesOpen ? "rotate-180" : ""}`}
                          />
                        </button>
                      </div>
                      <AnimatePresence>
                        {mobileServicesOpen && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="overflow-hidden pl-3"
                          >
                            <div className="py-2 flex flex-col gap-1 border-l-2 border-slate-200 pl-3">
                              {SERVICE_KEYS.map((k) => {
                                const meta = SERVICE_META[k];
                                const Icon = SERVICE_ICONS[meta.icon];
                                return (
                                  <Link
                                    key={k}
                                    to={`/dienstleistungen/${k}`}
                                    data-testid={`mobile-dropdown-service-${k}`}
                                    onClick={() => {
                                      setMobileOpen(false);
                                      setMobileServicesOpen(false);
                                    }}
                                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold text-slate-700 hover:bg-slate-100"
                                  >
                                    <span
                                      className="h-8 w-8 rounded-lg flex items-center justify-center"
                                      style={{ backgroundColor: `${meta.color}15`, color: meta.color }}
                                    >
                                      <Icon className="h-4 w-4" />
                                    </span>
                                    {t.services[k].name}
                                  </Link>
                                );
                              })}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                }
                return (
                  <NavLink
                    key={link.to}
                    to={link.to}
                    data-testid={`mobile-${link.testid}`}
                    end={link.to === "/"}
                    onClick={() => setMobileOpen(false)}
                    className={({ isActive }) =>
                      `px-4 py-3 rounded-xl text-base font-semibold transition-colors ${
                        isActive ? "bg-[#0022FF] text-white" : "text-slate-700 hover:bg-slate-100"
                      }`
                    }
                  >
                    {link.label}
                  </NavLink>
                );
              })}
              <a
                href={`tel:${CONTACT_INFO.phoneClean}`}
                data-testid="mobile-header-call-btn"
                className="mt-2 flex items-center justify-center gap-2 rounded-full btn-shk-primary px-5 py-3 text-sm font-semibold"
              >
                <Phone className="h-4 w-4" /> {t.nav.callNow} · {CONTACT_INFO.phone}
              </a>
              <a
                href={buildQuoteMailto(lang)}
                data-testid="mobile-header-email-btn"
                className="flex items-center justify-center gap-2 rounded-full border border-slate-300 bg-white px-5 py-3 text-sm font-semibold text-slate-800 hover:border-[#0022FF] hover:text-[#0022FF] transition-colors"
              >
                <Mail className="h-4 w-4" /> {t.nav.emailQuote}
              </a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
